/**
 * Advanced feature flag definitions.
 *
 * This file lists the "advanced features" a user can turn on or off for a
 * template, like strict focus mode and snapping. The settings panel
 * reads `ADVANCED_FEATURES` to render one toggle per feature, and the
 * editor store checks `FEATURE_KEYS` to know which flag is which.
 *
 * The one thing to remember: each entry is just data (label, icon, default)
 * plus an optional `onDisable` cleanup callback that runs when a user
 * switches the feature off.
 */
import type { Store } from 'pinia';
import type { FeatureDefinition } from '@/types/ui/featureFlag';

export const FEATURE_KEYS = {
  STRICT_FOCUS_MODE: 'strictFocusMode',
  SNAPPING: 'snapping',
  SHOW_LAYERS: 'showLayers',
  AUTOSAVE: 'autosave'
} as const;

export const ADVANCED_FEATURES: FeatureDefinition[] = [
  {
    key: FEATURE_KEYS.STRICT_FOCUS_MODE,
    labelKey: 'labels.strictFocusMode',
    icon: 'center_focus_strong',
    default: false,
    onDisable: store => {
      (store as any).strictFocusAdId = null;
    }
  },
  {
    key: FEATURE_KEYS.SNAPPING,
    labelKey: 'labels.snapping',
    icon: 'grid_4x4',
    // Default ON: covers the whole snap system — magnetic snap, guide
    // lines, Shift hover measuring, and keyboard nudge guides.
    default: true
  },
  {
    key: FEATURE_KEYS.SHOW_LAYERS,
    labelKey: 'labels.showLayers',
    icon: 'layers',
    // The panel itself stays reachable from the canvas menu either way;
    // this only puts it in the rail.
    default: false
  },
  {
    key: FEATURE_KEYS.AUTOSAVE,
    labelKey: 'labels.autosave',
    icon: 'cloud_sync',
    // Default ON, per template. sync-api reads `featureFlags.autosave` from
    // the room document and stops its periodic / idle / handover / recovery
    // saves while it is false; the save chip's "Save now" still works.
    default: true
  }
];
